import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useTheme } from '../../../assets/contexts/ThemeContext';
import { applyAppLanguage } from '../../../assets/utils/comm/languageUtils';
import { SUPPORTED_APP_LANGUAGES } from '../../../assets/utils/comm/languageConstants';

const LANGUAGE_META = {
  ko: { flag: '🇰🇷', labelKey: 'language.ko' },
  en: { flag: '🇺🇸', labelKey: 'language.en' },
};

/**
 * 헤더 상단 — Language 콤보(드롭다운) 언어 선택
 */
const HeaderLanguageSwitcher = memo(({ textColor = '#374151', className = '', menuPlacement = 'down' }) => {
  const { i18n, t } = useTranslation();
  const { pathname, search } = useLocation();
  const themeContext = useTheme();
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);

  const current = String(i18n.language || 'ko').startsWith('en') ? 'en' : 'ko';

  const options = useMemo(
    () =>
      SUPPORTED_APP_LANGUAGES.map((code) => ({
        code,
        flag: LANGUAGE_META[code]?.flag ?? '',
        label: t(LANGUAGE_META[code]?.labelKey ?? code, code),
      })),
    [t]
  );

  const handleSelect = useCallback(
    (lang) => {
      setOpen(false);
      if (lang === current) return;
      applyAppLanguage(lang, {
        onThemeLocale: (locale) => themeContext?.set?.('locale', locale, false),
        updateUrl: true,
        pathname,
        search,
      });
    },
    [current, pathname, search, themeContext]
  );

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocClick, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className={`header-top-lang-combo${className ? ` ${className}` : ''}`} ref={rootRef}>
      <button
        type="button"
        className={`header-top-lang-combo__trigger${open ? ' is-open' : ''}`}
        style={{ color: textColor }}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('language.switchAria', '언어 선택')}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="header-top-lang-combo__globe" aria-hidden="true">
          <i className="ti-world" />
        </span>
        <span className="header-top-lang-combo__label">{t('language.comboLabel', 'Language')}</span>
        <span className="header-top-lang-combo__chevron" aria-hidden="true">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>

      {open && (
        <ul
          className={`header-top-lang-combo__menu${
            menuPlacement === 'up' ? ' header-top-lang-combo__menu--up' : ''
          }`}
          role="listbox"
          aria-label={t('language.switchAria', '언어 선택')}
        >
          {options.map((opt) => {
            const selected = opt.code === current;
            return (
              <li key={opt.code} role="none">
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={`header-top-lang-combo__option${selected ? ' is-selected' : ''}`}
                  onClick={() => handleSelect(opt.code)}
                >
                  <span className="header-top-lang-combo__flag" aria-hidden="true">
                    {opt.flag}
                  </span>
                  <span className="header-top-lang-combo__option-label">{opt.label}</span>
                  {selected && (
                    <span className="header-top-lang-combo__check" aria-hidden="true">
                      ✓
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <style>{`
        .header-top-lang-combo {
          position: relative;
          display: inline-flex;
          margin-left: 6px;
          flex-shrink: 0;
        }
        .header-top-lang-combo__trigger {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          border: none;
          background: transparent;
          padding: 3px 6px;
          font-size: 11px;
          font-weight: 500;
          line-height: 1;
          cursor: pointer;
          white-space: nowrap;
          border-radius: 4px;
          transition: background-color 0.15s ease, opacity 0.15s ease;
        }
        .header-top-lang-combo__trigger:hover,
        .header-top-lang-combo__trigger.is-open {
          background: rgba(0, 0, 0, 0.06);
        }
        .header-top-lang-combo__globe {
          display: inline-flex;
          align-items: center;
          font-size: 12px;
          opacity: 0.9;
        }
        .header-top-lang-combo__globe i[class^="ti-"] {
          font-size: 12px;
          line-height: 1;
        }
        .header-top-lang-combo__label {
          font-size: 11px;
          font-weight: 500;
        }
        .header-top-lang-combo__chevron {
          display: inline-flex;
          align-items: center;
          opacity: 0.75;
          transition: transform 0.15s ease;
        }
        .header-top-lang-combo__trigger.is-open .header-top-lang-combo__chevron {
          transform: rotate(180deg);
        }
        .header-top-lang-combo__menu {
          position: absolute;
          top: calc(100% + 6px);
          left: 0;
          z-index: 1200;
          min-width: 148px;
          margin: 0;
          padding: 6px 0;
          list-style: none;
          background: #fff;
          border: 1px solid rgba(0, 0, 0, 0.08);
          border-radius: 6px;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
        }
        .header-top-lang-combo__option {
          display: flex;
          align-items: center;
          gap: 8px;
          width: 100%;
          border: none;
          background: transparent;
          padding: 8px 14px;
          font-size: 13px;
          font-weight: 500;
          color: #333;
          text-align: left;
          cursor: pointer;
          transition: background-color 0.12s ease;
        }
        .header-top-lang-combo__option:hover {
          background: #f3f4f6;
        }
        .header-top-lang-combo__option.is-selected {
          color: var(--theme-color-primary, #1565c0);
          font-weight: 600;
        }
        .header-top-lang-combo__flag {
          font-size: 16px;
          line-height: 1;
          flex-shrink: 0;
        }
        .header-top-lang-combo__option-label {
          flex: 1;
          min-width: 0;
        }
        .header-top-lang-combo__check {
          font-size: 12px;
          color: var(--theme-color-primary, #1565c0);
          flex-shrink: 0;
        }
        .header-top-lang-combo__menu--up {
          top: auto;
          bottom: calc(100% + 6px);
        }
        .header-top-lang-combo.mobile-menu-lang-combo {
          margin-left: 0;
          width: auto;
        }
        .header-top-lang-combo.mobile-menu-lang-combo .header-top-lang-combo__trigger {
          width: auto;
          justify-content: center;
          min-height: 0;
          padding: 4px 2px;
          font-size: 13px;
          border-radius: 4px;
          background: transparent;
        }
        .header-top-lang-combo.mobile-menu-lang-combo .header-top-lang-combo__label {
          font-size: 13px;
        }
        .header-top-lang-combo.mobile-menu-lang-combo .header-top-lang-combo__menu {
          left: 50%;
          right: auto;
          transform: translateX(-50%);
          min-width: 148px;
          width: auto;
        }
      `}</style>
    </div>
  );
});

HeaderLanguageSwitcher.displayName = 'HeaderLanguageSwitcher';

export default HeaderLanguageSwitcher;
